/**
 * The three plan writes that more than one surface makes: add a week, mark the plan completed,
 * shift what is left of it.
 *
 * Promoted out of `pages/assigned-plan-calendar/lib/usePlanActs.ts` on 24 September 2026, when the
 * task cards became the second surface to make them — "Dodaj tydzień" on a plan that is ending,
 * „Zakończ plan" on one whose schedule ran out, „Przesuń resztę planu o tydzień" on a client with
 * an empty calendar. A page may not import a page, so the three moved here and the calendar's
 * dialog calls them.
 *
 * ── Every success invalidates what the write changed, here and not at the caller ─────────────
 * An added week moves the plan's end on the execution summary, on the client's assignment list,
 * on the client detail's header and, often, off the attention queue (a plan no longer ending soon
 * is no longer a task). Leaving the invalidation to each caller is how the first copy of this
 * hook left the plan picker reading `Aktywny` over a grid that had already changed.
 *
 * ── A PROMISE, not a `mutate` with callbacks ─────────────────────────────────────────────────
 * The two callers settle differently — the calendar closes its dialog and says so, a task card
 * strikes itself only once the server has agreed (`features/attention-queue`'s implicit close) —
 * so what they share is the write and its invalidation, and each reads the outcome its own way.
 * A refusal rejects; the caller says it.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { invalidateAssignmentExecution } from '~/entities/plan-assignment';

import { attentionQueueQueryKey, clientDetailRoot } from '~/entities/client';
import {
  getGetPlanAssignmentCustomerCustomerIdQueryKey,
  postPlanAssignmentIdAddWeek,
  postPlanAssignmentIdComplete,
  postPlanAssignmentIdShiftRemaining,
} from '~/shared/api/generated/endpoints/plan-assignment/plan-assignment';
import { useContentLanguage } from '~/shared/i18n/contentLanguage';

/** Which assignment, and whose — the client is what the assignment list and the header key on. */
export interface PlanTarget {
  readonly assignmentId: number;
  /** `null` where the caller does not know the client; the per-client reads are then left alone. */
  readonly clientId: number | null;
}

export interface PlanShift {
  /** Signed calendar days. The task card's "o tydzień" is `7`. */
  readonly days: number;
  /** The not-started workout the shift starts from; omitted means every not-started session. */
  readonly fromWorkoutId?: number;
}

export interface PlanLifecycle {
  readonly addWeek: (target: PlanTarget) => Promise<void>;
  readonly completePlan: (target: PlanTarget) => Promise<void>;
  readonly shiftRemaining: (target: PlanTarget, shift: PlanShift) => Promise<void>;
  /** Any of the three is in flight. */
  readonly planWritePending: boolean;
}

export function usePlanLifecycle(): PlanLifecycle {
  const contentLang = useContentLanguage();
  const queryClient = useQueryClient();

  const settle = ({ assignmentId, clientId }: PlanTarget) => {
    // The generated key with no params is a PREFIX of every language's entry.
    invalidateAssignmentExecution(queryClient, assignmentId);
    void queryClient.invalidateQueries({ queryKey: attentionQueueQueryKey });
    if (clientId !== null) {
      void queryClient.invalidateQueries({
        queryKey: getGetPlanAssignmentCustomerCustomerIdQueryKey(clientId),
      });
      void queryClient.invalidateQueries({ queryKey: clientDetailRoot(clientId) });
    }
  };

  const addWeek = useMutation({
    mutationFn: (target: PlanTarget) =>
      postPlanAssignmentIdAddWeek(target.assignmentId, { lang: contentLang }),
    onSuccess: (_answer, target) => {
      settle(target);
    },
  });
  const complete = useMutation({
    mutationFn: (target: PlanTarget) =>
      postPlanAssignmentIdComplete(target.assignmentId, { lang: contentLang }),
    onSuccess: (_answer, target) => {
      settle(target);
    },
  });
  const shift = useMutation({
    mutationFn: ({ target, by }: { readonly target: PlanTarget; readonly by: PlanShift }) =>
      postPlanAssignmentIdShiftRemaining(target.assignmentId, {
        days: by.days,
        // Omitted entirely for "every not-started session", which is the endpoint's own default.
        ...(by.fromWorkoutId === undefined ? {} : { fromWorkoutId: by.fromWorkoutId }),
      }),
    onSuccess: (_answer, { target }) => {
      settle(target);
    },
  });

  return {
    addWeek: async (target) => {
      await addWeek.mutateAsync(target);
    },
    completePlan: async (target) => {
      await complete.mutateAsync(target);
    },
    shiftRemaining: async (target, by) => {
      await shift.mutateAsync({ target, by });
    },
    planWritePending: addWeek.isPending || complete.isPending || shift.isPending,
  };
}
